import { useCallback, useMemo, useState, type ReactNode } from 'react'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined'
import DashboardOutlined from '@mui/icons-material/DashboardOutlined'
import HubOutlined from '@mui/icons-material/HubOutlined'
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined'
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined'
import GroupAddOutlined from '@mui/icons-material/GroupAddOutlined'
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined'
import SchoolOutlined from '@mui/icons-material/SchoolOutlined'
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined'
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded'
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined'
import VideoCallOutlined from '@mui/icons-material/VideoCallOutlined'
import WorkOutline from '@mui/icons-material/WorkOutlineRounded'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'

import type { Workspace, WorkspaceRole } from '../../../types/workspace'
import {
  getBuiltInDefaultTabs,
  inferUseCaseFromModules,
  isTabModuleActive,
  resolveTabBar,
  type LockMode,
  type LockSpec,
  type WorkspaceTabDefaults,
} from '../../../services/navigation/tabBarEngine'
import {
  HELPER_TAB_CATALOG,
  MEMBER_TAB_CATALOG,
  type Surface,
  type TabDef,
  type TabKey,
} from '../../../services/navigation/tabCatalog'
import { saveWorkspaceTabDefaults } from '../../../services/navigation/tabPrefsService'
import { useNls } from '../../../nls/nlsProvider'
import { useToast } from '../../../context/useToast'
import { Button, Pressable, Select, type SelectOption } from '../../Common'
import './WorkspaceTabBarDefaultsPanel.css'

export type ConfigTargetRole = 'ADMIN' | 'EDITOR' | 'VIEWER' | 'HELPER'

export interface WorkspaceTabBarDefaultsPanelProps {
  workspaceId: string
  workspace?: Workspace
  defaults?: WorkspaceTabDefaults
  canEdit: boolean
  isSaving?: boolean
  onChange?: (next: WorkspaceTabDefaults) => void
  onSaved?: () => void
  isMobile?: boolean
}

const getTabIcon = (key: TabKey): ReactNode => {
  const iconStyle = { fontSize: 18 }
  switch (key) {
    case 'for-you':
    case 'tasks':
      return <TaskAltOutlined style={iconStyle} />
    case 'board':
      return <DashboardOutlined style={iconStyle} />
    case 'nexus':
      return <HubOutlined style={iconStyle} />
    case 'ai':
      return <AutoAwesomeOutlined style={iconStyle} />
    case 'forms':
      return <DescriptionOutlined style={iconStyle} />
    case 'sync':
      return <SyncOutlinedIcon style={iconStyle} />
    case 'automations':
      return <SmartToyOutlined style={iconStyle} />
    case 'team':
      return <GroupAddOutlined style={iconStyle} />
    case 'stock':
      return <Inventory2Outlined style={iconStyle} />
    case 'class':
    case 'school':
      return <SchoolOutlined style={iconStyle} />
    case 'field':
      return <LocationOnOutlined style={iconStyle} />
    case 'chat':
      return <ChatBubbleOutlineRounded style={iconStyle} />
    case 'calendar':
      return <CalendarTodayOutlined style={iconStyle} />
    case 'meetings':
      return <VideoCallOutlined style={iconStyle} />
    case 'ops':
      return <WorkOutline style={iconStyle} />
    default:
      return null
  }
}

export const WorkspaceTabBarDefaultsPanel = ({
  workspaceId,
  workspace,
  defaults,
  canEdit,
  isSaving = false,
  onChange,
  onSaved,
}: WorkspaceTabBarDefaultsPanelProps) => {
  const { t } = useNls()
  const { showToast } = useToast()

  const [targetRole, setTargetRole] = useState<ConfigTargetRole>('ADMIN')
  const [draft, setDraft] = useState<WorkspaceTabDefaults>(() => defaults ?? {})
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false)
  const [justSaved, setJustSaved] = useState(false)
  const [isSavingInternal, setIsSavingInternal] = useState(false)

  const [prevDefaults, setPrevDefaults] = useState(defaults)
  if (defaults !== prevDefaults) {
    setPrevDefaults(defaults)
    if (!hasUnsavedEdits) {
      setDraft(defaults ?? {})
    }
  }

  const disabled = !canEdit || isSaving || isSavingInternal

  const surface: Surface = targetRole === 'HELPER' ? 'helper' : 'member'
  const effectiveRole: WorkspaceRole = targetRole === 'HELPER' ? 'HELPER' : targetRole
  const catalog: readonly TabDef[] = surface === 'helper' ? HELPER_TAB_CATALOG : MEMBER_TAB_CATALOG

  // Resolve current active pinned list for selected target
  const currentPinned: TabKey[] = useMemo(() => {
    const useCase = inferUseCaseFromModules(workspace)
    if (surface === 'helper') {
      return draft.helper ?? getBuiltInDefaultTabs('helper', useCase)
    }
    const memberDefaults = draft.member?.[targetRole as 'ADMIN' | 'EDITOR' | 'VIEWER']
    return memberDefaults ?? getBuiltInDefaultTabs('member', useCase)
  }, [draft, surface, targetRole, workspace])

  // Resolve current locks for selected target
  const currentLocks: LockSpec[] = useMemo(() => {
    if (surface === 'helper') {
      return draft.locks?.helper ?? []
    }
    return draft.locks?.member?.[targetRole as 'ADMIN' | 'EDITOR' | 'VIEWER'] ?? []
  }, [draft, surface, targetRole])

  const lockMap = useMemo(() => {
    const map = new Map<TabKey, LockMode>()
    for (const spec of currentLocks) {
      map.set(spec.key, spec.mode)
    }
    return map
  }, [currentLocks])

  // Mutate draft pinned list for target role deriving from prev
  const updatePinned = (nextPinnedOrUpdater: TabKey[] | ((prevPinned: TabKey[]) => TabKey[])) => {
    setDraft((prev) => {
      const useCase = inferUseCaseFromModules(workspace)
      let nextPinned: TabKey[]
      let next: WorkspaceTabDefaults
      if (surface === 'helper') {
        const prevPinned = prev.helper ?? getBuiltInDefaultTabs('helper', useCase)
        nextPinned = typeof nextPinnedOrUpdater === 'function' ? nextPinnedOrUpdater(prevPinned) : nextPinnedOrUpdater
        next = { ...prev, helper: nextPinned }
      } else {
        const role = targetRole as 'ADMIN' | 'EDITOR' | 'VIEWER'
        const prevPinned = prev.member?.[role] ?? getBuiltInDefaultTabs('member', useCase)
        nextPinned = typeof nextPinnedOrUpdater === 'function' ? nextPinnedOrUpdater(prevPinned) : nextPinnedOrUpdater
        next = {
          ...prev,
          member: {
            ...prev.member,
            [role]: nextPinned,
          },
        }
      }
      setHasUnsavedEdits(true)
      setJustSaved(false)
      onChange?.(next)
      return next
    })
  }

  // Mutate lock mode for a tab deriving from prev
  const setLock = (key: TabKey, mode: 'none' | LockMode) => {
    setDraft((prev) => {
      let next: WorkspaceTabDefaults
      if (surface === 'helper') {
        const prevLocks = prev.locks?.helper ?? []
        const without = prevLocks.filter((l) => l.key !== key)
        const nextLocksForRole = mode === 'none' ? without : [...without, { key, mode }]

        // Locking a tab removes it from the role's pinned list only when that role has an explicit list.
        // It never writes the built-in list as a new explicit default.
        const nextHelper = prev.helper !== undefined && mode !== 'none'
          ? prev.helper.filter((k) => k !== key)
          : prev.helper

        next = { ...prev }
        if (nextHelper !== undefined) {
          next.helper = nextHelper
        }
        const nextLocks: WorkspaceTabDefaults['locks'] = { ...prev.locks }
        if (nextLocksForRole.length > 0) {
          nextLocks.helper = nextLocksForRole
        } else {
          delete nextLocks.helper
        }
        if (Object.keys(nextLocks).length > 0 && (nextLocks.member || nextLocks.helper)) {
          next.locks = nextLocks
        } else {
          delete next.locks
        }
      } else {
        const role = targetRole as 'ADMIN' | 'EDITOR' | 'VIEWER'
        const prevLocks = prev.locks?.member?.[role] ?? []
        const without = prevLocks.filter((l) => l.key !== key)
        const nextLocksForRole = mode === 'none' ? without : [...without, { key, mode }]

        // Locking a tab removes it from the role's pinned list only when that role has an explicit list.
        // It never writes the built-in list as a new explicit default.
        const explicitMember = prev.member?.[role]
        const nextMemberForRole = explicitMember !== undefined && mode !== 'none'
          ? explicitMember.filter((k) => k !== key)
          : explicitMember

        next = { ...prev }
        if (nextMemberForRole !== undefined) {
          next.member = { ...prev.member, [role]: nextMemberForRole }
        }
        const memberLocks = { ...prev.locks?.member }
        if (nextLocksForRole.length > 0) {
          memberLocks[role] = nextLocksForRole
        } else {
          delete memberLocks[role]
        }
        const nextLocks: WorkspaceTabDefaults['locks'] = { ...prev.locks }
        if (Object.keys(memberLocks).length > 0) {
          nextLocks.member = memberLocks
        } else {
          delete nextLocks.member
        }
        if (Object.keys(nextLocks).length > 0 && (nextLocks.member || nextLocks.helper)) {
          next.locks = nextLocks
        } else {
          delete next.locks
        }
      }
      setHasUnsavedEdits(true)
      setJustSaved(false)
      onChange?.(next)
      return next
    })
  }

  const moveUp = (index: number) => {
    if (index <= 1) return // index 0 is fixed first
    updatePinned((prevPinned) => {
      if (index <= 1 || index >= prevPinned.length) return prevPinned
      const next = [...prevPinned]
      const temp = next[index]
      next[index] = next[index - 1]
      next[index - 1] = temp
      return next
    })
  }

  const moveDown = (index: number) => {
    updatePinned((prevPinned) => {
      if (index <= 0 || index >= prevPinned.length - 1) return prevPinned
      const next = [...prevPinned]
      const temp = next[index]
      next[index] = next[index + 1]
      next[index + 1] = temp
      return next
    })
  }

  const unpin = (key: TabKey) => {
    updatePinned((prevPinned) => {
      if (prevPinned[0] === key) return prevPinned
      return prevPinned.filter((k) => k !== key)
    })
  }

  const pin = (key: TabKey) => {
    updatePinned((prevPinned) => {
      if (prevPinned.length >= 4) return prevPinned
      if (prevPinned.includes(key)) return prevPinned
      return [...prevPinned, key]
    })
  }

  const handleReset = () => {
    setDraft((prev) => {
      let next: WorkspaceTabDefaults
      if (surface === 'helper') {
        const rest = { ...prev }
        delete rest.helper
        const nextLocks = prev.locks ? { ...prev.locks } : undefined
        if (nextLocks) {
          delete nextLocks.helper
        }
        next = rest
        if (nextLocks && nextLocks.member && Object.keys(nextLocks.member).length > 0) {
          next.locks = nextLocks
        } else {
          delete next.locks
        }
      } else {
        const role = targetRole as 'ADMIN' | 'EDITOR' | 'VIEWER'
        const nextMember = prev.member ? { ...prev.member } : undefined
        if (nextMember) {
          delete nextMember[role]
        }
        const nextMemberLocks = prev.locks?.member ? { ...prev.locks.member } : undefined
        if (nextMemberLocks) {
          delete nextMemberLocks[role]
        }
        next = { ...prev }
        if (nextMember && Object.keys(nextMember).length > 0) {
          next.member = nextMember
        } else {
          delete next.member
        }
        const nextLocks: WorkspaceTabDefaults['locks'] = {}
        if (nextMemberLocks && Object.keys(nextMemberLocks).length > 0) {
          nextLocks.member = nextMemberLocks
        }
        if (prev.locks?.helper && prev.locks.helper.length > 0) {
          nextLocks.helper = prev.locks.helper
        }
        if (Object.keys(nextLocks).length > 0) {
          next.locks = nextLocks
        } else {
          delete next.locks
        }
      }
      setHasUnsavedEdits(true)
      setJustSaved(false)
      onChange?.(next)
      return next
    })
  }

  const handleSave = async () => {
    if (disabled) return
    setIsSavingInternal(true)
    try {
      await saveWorkspaceTabDefaults(workspaceId, draft)
      const msg = t('NLSWorkspaceTabBarDefaultsSaved')
      setJustSaved(true)
      setHasUnsavedEdits(false)
      showToast(msg, 'success')
      onSaved?.()
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : t('NLSWorkspaceTabBarDefaultsSaveError')
      showToast(errMsg, 'error')
    } finally {
      setIsSavingInternal(false)
    }
  }

  // Live 4 + More preview calling resolveTabBar with workspace
  const preview = useMemo(() => {
    return resolveTabBar({
      surface,
      role: effectiveRole,
      workspace,
      workspaceDefaults: draft,
    })
  }, [draft, effectiveRole, surface, workspace])

  const targetRoleOptions: { key: ConfigTargetRole; label: string }[] = [
    { key: 'ADMIN', label: t('NLSWorkspaceTabBarDefaultsRoleAdmin') },
    { key: 'EDITOR', label: t('NLSWorkspaceTabBarDefaultsRoleEditor') },
    { key: 'VIEWER', label: t('NLSWorkspaceTabBarDefaultsRoleViewer') },
    { key: 'HELPER', label: t('NLSWorkspaceTabBarDefaultsRoleHelper') },
  ]

  const lockOptions: SelectOption<string>[] = [
    { value: 'none', label: t('NLSWorkspaceTabBarDefaultsLockNone') },
    { value: 'hidden', label: t('NLSWorkspaceTabBarDefaultsLockHidden') },
    { value: 'visible-off', label: t('NLSWorkspaceTabBarDefaultsLockVisibleOff') },
  ]

  const availableCatalogTabs = catalog.filter(
    (tab) => tab.available !== false && isTabModuleActive(tab, workspace) && !currentPinned.includes(tab.key),
  )

  const getTabLabel = useCallback(
    (tabDef: TabDef | undefined, fallbackKey: string): string => {
      if (!tabDef) return fallbackKey
      const key = tabDef.labelKeyFor
        ? tabDef.labelKeyFor({ workspace, modules: workspace?.modules })
        : tabDef.labelKey
      return t(key)
    },
    [workspace, t],
  )

  return (
    <div className="workspaceSettingsRows workspaceTabBarDefaultsPanel" data-testid="workspace-tab-bar-defaults-panel">
      {!canEdit && (
        <article className="settingsCacheBlock" role="status">
          <strong>{t('NLSWorkspaceTabBarDefaultsReadOnly')}</strong>
          <p>{t('NLSWorkspaceTabBarDefaultsSubtitle')}</p>
        </article>
      )}

      {/* Target role selector */}
      <div className="workspaceSettingsRow workspaceTabBarDefaultsPanel__roleSelector">
        <span>
          <strong>{t('NLSWorkspaceTabBarDefaultsRoleHeader')}</strong>
          <small>{t('NLSWorkspaceTabBarDefaultsRoleSubhead')}</small>
        </span>
        <div className="workspaceTabBarDefaultsPanel__rolePills" role="tablist" aria-label={t('NLSWorkspaceTabBarDefaultsRoleTargetAria')}>
          {targetRoleOptions.map((opt) => (
            <Pressable
              key={opt.key}
              role="tab"
              aria-selected={targetRole === opt.key}
              className={`workspaceTabBarDefaultsPanel__rolePill ${targetRole === opt.key ? 'isActive' : ''}`}
              onClick={() => setTargetRole(opt.key)}
              data-testid={`target-role-${opt.key.toLowerCase()}`}
            >
              {opt.label}
            </Pressable>
          ))}
        </div>
      </div>

      {/* Pinned tabs list */}
      <div className="workspaceSettingsRow workspaceTabBarDefaultsPanel__section">
        <span>
          <strong>{t('NLSWorkspaceTabBarDefaultsPinnedHeader', { count: currentPinned.length })}</strong>
          <small>{t('NLSWorkspaceTabBarDefaultsPinnedSubhead')}</small>
        </span>
        <div className="workspaceTabBarDefaultsPanel__list" data-testid="pinned-tab-list">
          {currentPinned.map((key, index) => {
            const tabDef = catalog.find((c) => c.key === key)
            const label = getTabLabel(tabDef, key)
            const isFixed = index === 0

            return (
              <div
                key={key}
                className="workspaceTabBarDefaultsPanel__row"
                data-testid={`pinned-tab-row-${key}`}
              >
                <div className="workspaceTabBarDefaultsPanel__itemInfo">
                  <span className="workspaceTabBarDefaultsPanel__itemIcon" aria-hidden="true">
                    {getTabIcon(key)}
                  </span>
                  <span className="workspaceTabBarDefaultsPanel__itemLabel">{label}</span>
                  {isFixed && (
                    <span className="workspaceTabBarDefaultsPanel__fixedBadge" title={t('NLSWorkspaceTabBarDefaultsFixedTitle')}>
                      <LockRoundedIcon fontSize="inherit" />
                      {t('NLSWorkspaceTabBarDefaultsFixedBadge')}
                    </span>
                  )}
                </div>

                <div className="workspaceTabBarDefaultsPanel__itemActions">
                  {!isFixed && (
                    <>
                      <Button
                        variant="tertiary"
                        size="sm"
                        disabled={disabled || index <= 1}
                        onClick={() => moveUp(index)}
                        aria-label={t('NLSWorkspaceTabBarDefaultsMoveUpAria', { label })}
                        icon={<KeyboardArrowUpRoundedIcon fontSize="small" />}
                        data-testid={`move-up-${key}`}
                      />
                      <Button
                        variant="tertiary"
                        size="sm"
                        disabled={disabled || index >= currentPinned.length - 1}
                        onClick={() => moveDown(index)}
                        aria-label={t('NLSWorkspaceTabBarDefaultsMoveDownAria', { label })}
                        icon={<KeyboardArrowDownRoundedIcon fontSize="small" />}
                        data-testid={`move-down-${key}`}
                      />
                      <Button
                        variant="tertiary"
                        size="sm"
                        disabled={disabled}
                        onClick={() => unpin(key)}
                        aria-label={t('NLSWorkspaceTabBarDefaultsRemoveAria', { label })}
                        icon={<RemoveRoundedIcon fontSize="small" />}
                        data-testid={`remove-${key}`}
                      />
                    </>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Add available tab to bar */}
      {availableCatalogTabs.length > 0 && currentPinned.length < 4 && (
        <div className="workspaceSettingsRow workspaceTabBarDefaultsPanel__section">
          <span>
            <strong>{t('NLSWorkspaceTabBarDefaultsAvailableHeader')}</strong>
            <small>{t('NLSWorkspaceTabBarDefaultsAvailableHint')}</small>
          </span>
          <div className="workspaceTabBarDefaultsPanel__availableGrid">
            {availableCatalogTabs.map((tab) => {
              const label = getTabLabel(tab, tab.key)
              const isLocked = lockMap.has(tab.key)

              return (
                <Button
                  key={tab.key}
                  variant="secondary"
                  size="sm"
                  disabled={disabled || isLocked}
                  onClick={() => pin(tab.key)}
                  icon={<AddRoundedIcon fontSize="small" />}
                  data-testid={`add-tab-${tab.key}`}
                >
                  {label}
                </Button>
              )
            })}
          </div>
        </div>
      )}

      {/* Lock this tab off for this role */}
      <div className="workspaceSettingsRow workspaceTabBarDefaultsPanel__section">
        <span>
          <strong>{t('NLSWorkspaceTabBarDefaultsLockOffFor')}</strong>
          <small>{t('NLSWorkspaceTabBarDefaultsLockHint')}</small>
        </span>
        <div className="workspaceTabBarDefaultsPanel__locksList" data-testid="tab-locks-list">
          {catalog
            .filter((tab) => tab.available !== false && isTabModuleActive(tab, workspace) && !tab.fixedFirst)
            .map((tab) => {
              const label = getTabLabel(tab, tab.key)
              const mode = lockMap.get(tab.key) ?? 'none'

              return (
                <div
                  key={tab.key}
                  className="workspaceTabBarDefaultsPanel__lockRow"
                  data-testid={`lock-row-${tab.key}`}
                >
                  <div className="workspaceTabBarDefaultsPanel__itemInfo">
                    <span className="workspaceTabBarDefaultsPanel__itemIcon" aria-hidden="true">
                      {getTabIcon(tab.key)}
                    </span>
                    <span className="workspaceTabBarDefaultsPanel__itemLabel">{label}</span>
                  </div>

                  <Select
                    trigger="pill"
                    size="sm"
                    ariaLabel={t('NLSWorkspaceTabBarDefaultsLockModeAria', { label })}
                    value={mode}
                    options={lockOptions}
                    disabled={disabled}
                    onChange={(val) => setLock(tab.key, val as 'none' | LockMode)}
                  />
                </div>
              )
            })}
        </div>
      </div>

      {/* Live Phone Preview */}
      <div className="workspaceSettingsRow workspaceTabBarDefaultsPanel__previewSection">
        <span>
          <strong>{t('NLSWorkspaceTabBarDefaultsPreviewTitle')}</strong>
          <small>
            {t('NLSWorkspaceTabBarDefaultsPreviewHint', {
              role: targetRoleOptions.find((o) => o.key === targetRole)?.label ?? '',
            })}
          </small>
        </span>
        <div className="workspaceTabBarDefaultsPanel__phonePreview" data-testid="live-phone-preview">
          <div className="workspaceTabBarDefaultsPanel__mockTabBar" role="region" aria-label={t('NLSWorkspaceTabBarDefaultsMockTabBarAria')}>
            {preview.pinned.map((key) => {
              const tabDef = catalog.find((c) => c.key === key)
              const label = getTabLabel(tabDef, key)
              return (
                <div key={key} className="workspaceTabBarDefaultsPanel__mockTabItem">
                  <span className="workspaceTabBarDefaultsPanel__mockTabIcon">{getTabIcon(key)}</span>
                  <span className="workspaceTabBarDefaultsPanel__mockTabLabel">{label}</span>
                </div>
              )
            })}
            {preview.more.length > 0 && (
              <div className="workspaceTabBarDefaultsPanel__mockTabItem">
                <span className="workspaceTabBarDefaultsPanel__mockTabIcon">
                  <MoreHorizRoundedIcon fontSize="small" />
                </span>
                <span className="workspaceTabBarDefaultsPanel__mockTabLabel">{t('NLSCommonTabMore')}</span>
              </div>
            )}
          </div>

          {preview.more.length > 0 && (
            <div className="workspaceTabBarDefaultsPanel__mockMoreList">
              <span className="workspaceTabBarDefaultsPanel__mockMoreTitle">{t('NLSWorkspaceTabBarDefaultsInMoreSheet')}</span>
              <div className="workspaceTabBarDefaultsPanel__mockMoreChips">
                {preview.more.map((key) => {
                  const tabDef = catalog.find((c) => c.key === key)
                  const label = getTabLabel(tabDef, key)
                  const lockedEntry = preview.locked.find((l) => l.key === key)

                  return (
                    <span
                      key={key}
                      className={`workspaceTabBarDefaultsPanel__mockMoreChip ${lockedEntry ? 'isLocked' : ''}`}
                    >
                      {lockedEntry ? <LockRoundedIcon fontSize="inherit" /> : null}
                      {label}
                      {lockedEntry ? <small>({t(lockedEntry.reasonKey)})</small> : null}
                    </span>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer action buttons: Reset defaults & Save */}
      <div className="workspaceSettingsRow workspaceSettingsRow--action workspaceTabBarDefaultsPanel__footerActions">
        <span>
          <strong>{t('NLSWorkspaceTabBarDefaultsReset')}</strong>
          <small>
            {t('NLSWorkspaceTabBarDefaultsResetHint', {
              role: targetRoleOptions.find((o) => o.key === targetRole)?.label ?? '',
            })}
          </small>
        </span>
        <div className="workspaceTabBarDefaultsPanel__actionGroup">
          <Button
            variant="tertiary"
            size="sm"
            icon={<RestartAltRoundedIcon fontSize="inherit" />}
            disabled={disabled}
            onClick={handleReset}
            data-testid="tab-bar-defaults-reset-btn"
          >
            {t('NLSWorkspaceTabBarDefaultsReset')}
          </Button>

          <Button
            variant="primary"
            size="sm"
            icon={justSaved ? <CheckRoundedIcon fontSize="inherit" /> : <SaveRoundedIcon fontSize="inherit" />}
            disabled={disabled}
            onClick={() => void handleSave()}
            data-testid="tab-bar-defaults-save-btn"
          >
            {t('NLSWorkspaceTabBarDefaultsSave')}
          </Button>
        </div>
      </div>
    </div>
  )
}
